/**
 * Decode a fire-animation "frame pack" (format `fsp1`) into typed arrays.
 *
 * The animation endpoints (with `?format=fsp1`) and frozen story-map
 * animations (`.fsp` files) send the rasters as one binary blob instead of
 * base64 inside JSON:
 *
 *   "FSP1" | uint32 LE header length | JSON header | array bytes
 *
 * The header is `{ meta, arrays: [{ name, dtype, shape, offset }] }`; every
 * array starts 8-byte aligned, so it is viewed in place — no base64 decode and
 * no copy. The body arrives gzip-encoded and the browser inflates it natively.
 * The rasters are cropped to the burned window: `meta.shape`/`meta.bounds`
 * describe that window, `meta.domain_bounds` the whole simulation grid.
 */

import type { RasterBounds, UrbanAnimationFields } from "../types/FireSimulation";

const MAGIC = "FSP1";

/** Pack header metadata (frame arrays carry the max_*, confidence members/frame_count). */
export interface FramePackMeta extends UrbanAnimationFields {
  shape: [number, number];
  bounds: RasterBounds;
  domain_bounds?: RasterBounds;
  frame_times_seconds: number[];
  max_ros?: number;
  max_intensity?: number;
  max_flame_height?: number;
  max_hour?: number;
  member?: number;
  members?: number;
  frame_count?: number;
}

export interface FramePackArrayInfo {
  name: string;
  dtype: "float32" | "uint8";
  shape: number[];
  offset: number;
}

export interface FramePack {
  meta: FramePackMeta;
  arrays: Record<string, Float32Array | Uint8Array>;
}

export function decodeFramePack(buffer: ArrayBuffer): FramePack {
  const head = new Uint8Array(buffer, 0, Math.min(8, buffer.byteLength));
  if (
    head.length < 8 ||
    String.fromCharCode(head[0], head[1], head[2], head[3]) !== MAGIC
  ) {
    throw new Error("Animation data is not in the expected format.");
  }
  const headerLength = new DataView(buffer).getUint32(4, true);
  const header = JSON.parse(
    new TextDecoder().decode(new Uint8Array(buffer, 8, headerLength))
  ) as { meta: FramePackMeta; arrays: FramePackArrayInfo[] };
  const base = 8 + headerLength;
  const arrays: FramePack["arrays"] = {};
  for (const a of header.arrays) {
    const count = a.shape.reduce((n, d) => n * d, 1);
    arrays[a.name] =
      a.dtype === "float32"
        ? new Float32Array(buffer, base + a.offset, count)
        : new Uint8Array(buffer, base + a.offset, count);
  }
  return { meta: header.meta, arrays };
}

/** Frozen story-map animations saved as packs carry this extension. */
export const isFramePackUrl = (url: string): boolean =>
  /\.fsp(?:$|[?#])/.test(url);
